import React from "react";
import useDebugInformation from "../../hooks/useDebugInformation";
import { useAppSelector } from "@store/hooks";
import { sanitizeEmail, sanitizeBusinessName, sanitizeFirstName, sanitizeLastName, sanitizeCity, sanitizeStreet, sanitizePostcode, sanitizePhoneNumber, sanitizeRelationship, sanitizeSortCode, sanitizeAccountNumber, sanitizeCompanyRegistration } from ".";

const namespace = "Validations";

// react hook to sanitize fields, accepts prefix, value and field name
export function useSanitizer(value: string, name: string, skip: boolean = false) {
    // set value to empty string
    const [sanitized, setSanitized] = React.useState(value);
    // useEffect hook to sanitize fields
    React.useEffect(() => {
        // if value is not empty
        if (value !== "" && !skip) {
            // switch statement to sanitize fields
            switch (name.toLowerCase()) {
                // if name is email
                case "email":
                    // set value to value sanitized
                    setSanitized(sanitizeEmail(value));
                    break;
                // // if name is business name
                case "businessname":
                    // set value to value sanitized
                    setSanitized(sanitizeBusinessName(value));
                    break;
                // // if name is first name
                case "firstname":
                    // set value to value sanitized
                    setSanitized(sanitizeFirstName(value));
                    break;
                // // if name is last name
                case "lastname":
                    // set value to value sanitized
                    setSanitized(sanitizeLastName(value));
                    break;
                // // if name is title
                // case "title":
                //     // set value to value sanitized
                //     setValue(sanitizeTitle(value));
                //     break;
                // // if name is city
                case "city":
                    // set value to value sanitized
                    setSanitized(sanitizeCity(value));
                    break;
                // if name is street
                case "street":
                    // set value to value sanitized
                    setSanitized(sanitizeStreet(value));
                    break;

                // if name is postcode
                case "postcode":
                    // set value to value sanitized
                    setSanitized(sanitizePostcode(value));
                    break;
                // if name is landline
                case "landline":
                    // set value to value sanitized
                    setSanitized(sanitizePhoneNumber(value));
                    break;
                // if name is mobile
                case "mobile":
                    // set value to value sanitized
                    setSanitized(sanitizePhoneNumber(value));
                    break;
                // if name is relationship
                case "relationship":
                    // set value to value sanitized
                    setSanitized(sanitizeRelationship(value));
                    break;
                // if name is sort code
                case "sortcode":
                    // set value to value sanitized
                    setSanitized(sanitizeSortCode(value));
                    break;
                // if name is account number
                case "accountnumber":
                    // set value to value sanitized
                    setSanitized(sanitizeAccountNumber(value));
                    break;
                // if name is company registration
                case "companyregistration":
                    // set value to value sanitized
                    setSanitized(sanitizeCompanyRegistration(value));
                    break;

                // else set value to value
                default:
                    setSanitized(value);
                    break;
            }
        }
        else
            setSanitized(value);
    }, [value, name, skip])

    // useDebugInformation hook returns sanitized value
    useDebugInformation(namespace, { sanitized, value, name });
    // log sanitized value
    // logger.debug(`${namespace} useSanitizer: name: ${name}`, `sanitized: ${sanitized}`);

    // return value
    return sanitized;
}

// create react hook to return highest valid state for step of type number using the switch statement
// returns -1 if not found
export const useHighestValidTabForStep = (step: number) => {
    //  get interface state for step
    const step1_1Valid = useAppSelector(({ state }) => state.Step1_1Valid)
    const step1_2Valid = useAppSelector(({ state }) => state.Step1_2Valid)
    const step1_3Valid = useAppSelector(({ state }) => state.Step1_3Valid)
    const step1_4Valid = useAppSelector(({ state }) => state.Step1_4Valid)
    const step2_1Valid = useAppSelector(({ state }) => state.Step2_1Valid)
    const step2_2Valid = useAppSelector(({ state }) => state.Step2_2Valid)
    const step2_3Valid = useAppSelector(({ state }) => state.Step2_3Valid)
    const step3_1Valid = useAppSelector(({ state }) => state.Step3_1Valid)
    const step3_2Valid = useAppSelector(({ state }) => state.Step3_2Valid)
    const step3_3Valid = useAppSelector(({ state }) => state.Step3_3Valid)
    // useState hook to set highest valid state for step
    const [highestValidStep, setHighestValidStep] = React.useState(-1)

    // useEffect hook to set highest valid state for step
    React.useEffect(() => {
        // use switch statement to set highest valid state for step
        // isStepComplete returns true if step is valid
        let isStepComplete = false
        let arrayOfSteps: boolean[] = []
        switch (step) {
            // if step is 1
            case 1:
                // indexOf returns -1 if not found
                // set arrayOfSteps to hold all the steps
                arrayOfSteps = [step1_1Valid, step1_2Valid, step1_3Valid, step1_4Valid]
                // set isStepComplete to true if does not contain false
                isStepComplete = !arrayOfSteps.includes(false)
                // set highestValidStep to length of array if isStepComplete is true otherwise set to 0 indexOf false in array
                setHighestValidStep(isStepComplete ? arrayOfSteps.length : arrayOfSteps.indexOf(false))
                break;
            // if step is 2
            case 2:
                // indexOf returns -1 if not found
                // set arrayOfSteps to hold all the steps
                arrayOfSteps = [step2_1Valid, step2_2Valid, step2_3Valid]
                // set isStepComplete to true if does not contain false
                isStepComplete = !arrayOfSteps.includes(false)
                // set highestValidStep to length of array if isStepComplete is true otherwise set to 0 indexOf false in array
                setHighestValidStep(isStepComplete ? arrayOfSteps.length : arrayOfSteps.indexOf(false))
                break;
            // if step is 3
            case 3:
                // indexOf returns -1 if not found
                // set arrayOfSteps to hold all the steps
                arrayOfSteps = [step3_1Valid, step3_2Valid, step3_3Valid]
                // set isStepComplete to true if does not contain false
                isStepComplete = !arrayOfSteps.includes(false)
                // set highestValidStep to length of array if isStepComplete is true otherwise set to 0 indexOf false in array
                setHighestValidStep(isStepComplete ? arrayOfSteps.length : arrayOfSteps.indexOf(false))
                break;
            // else return -1
            default:
                // set highest valid state for step to -1
                setHighestValidStep(-1);
        }
    }, [step, step1_1Valid, step1_2Valid, step1_3Valid, step1_4Valid, step2_1Valid, step2_2Valid, step2_3Valid, step3_1Valid, step3_2Valid, step3_3Valid])
    // return highest valid state for step
    return highestValidStep
}

// create react hook to return array of tabs with valid states for step of type number using the switch statement
// returns empty array if not found
export const useTabsWithValidStatesForStep = (step: number) => {
    // use useInterface hook to get interface state for step
    const step1_1Valid = useAppSelector(({ state }) => state.Step1_1Valid)
    const step1_2Valid = useAppSelector(({ state }) => state.Step1_2Valid)
    const step1_3Valid = useAppSelector(({ state }) => state.Step1_3Valid)
    const step1_4Valid = useAppSelector(({ state }) => state.Step1_4Valid)

    const step2_1Valid = useAppSelector(({ state }) => state.Step2_1Valid)
    const step2_2Valid = useAppSelector(({ state }) => state.Step2_2Valid)
    const step2_3Valid = useAppSelector(({ state }) => state.Step2_3Valid)

    const step3_1Valid = useAppSelector(({ state }) => state.Step3_1Valid)
    const step3_2Valid = useAppSelector(({ state }) => state.Step3_2Valid)
    const step3_3Valid = useAppSelector(({ state }) => state.Step3_3Valid)
    const isBusiness = useAppSelector(({ ui }) => ui.PropertyAddressIsBusiness)

    // useState hook to set array of tabs with valid states for step
    const [tabsWithValidStates, setTabsWithValidStates] = React.useState<boolean[]>([])

    // useEffect hook to set array of tabs with valid states for step
    React.useEffect(() => {
        // use switch statement to set array of tabs with valid states for step
        // isStepComplete returns true if step is valid
        let arrayOfSteps: boolean[] = []
        switch (step) {
            // if step is 1
            case 1:
                // indexOf returns -1 if not found
                // set arrayOfSteps to hold all the steps
                arrayOfSteps = [step1_1Valid, step1_2Valid, step1_3Valid, step1_4Valid]
                // set tabsWithValidStates to arrayOfSteps
                setTabsWithValidStates(arrayOfSteps)
                break;
            // if step is 2
            case 2:
                // indexOf returns -1 if not found
                // set arrayOfSteps to hold all the steps
                arrayOfSteps = [step2_1Valid, step2_2Valid, step2_3Valid]
                // set tabsWithValidStates to arrayOfSteps
                setTabsWithValidStates(arrayOfSteps)
                break;
            // if step is 3
            case 3:
                // indexOf returns -1 if not found
                // set arrayOfSteps to hold all the steps
                arrayOfSteps = !isBusiness ? [step3_1Valid, step3_2Valid, step3_3Valid] : [step3_1Valid, step3_3Valid, step3_2Valid]
                // set tabsWithValidStates to arrayOfSteps
                setTabsWithValidStates(arrayOfSteps)
                break;
            // else return empty array
            default:
                // set tabsWithValidStates to empty array of boolean
                setTabsWithValidStates(arrayOfSteps)
        }

    }, [isBusiness, step, step1_1Valid, step1_2Valid, step1_3Valid, step1_4Valid, step2_1Valid, step2_2Valid, step2_3Valid, step3_1Valid, step3_2Valid, step3_3Valid])
    // return array of tabs with valid states for step
    return tabsWithValidStates
}